import { registerPlugin, type PluginListenerHandle } from '@capacitor/core'
import { isNativePlatform } from '@/lib/nativePlatform'

/**
 * JS bridge to the native `BusswerksAudio` Capacitor plugin.
 *
 * WKWebView on iOS / iPadOS / Mac Catalyst hides
 * `navigator.mediaDevices` on custom-scheme origins (our app loads
 * from `capacitor://localhost`), so the web's device-enumeration
 * path returns undefined there. The native plugin owns AVAudioSession
 * on the Swift side and exposes its device lists to JS so the
 * AudioPanel can populate pickers regardless.
 *
 * On web this wrapper never talks to the plugin — `isNativePlatform()`
 * gates the bridge call and the caller falls back to the browser's
 * own APIs (`navigator.mediaDevices.enumerateDevices`). Chrome /
 * Safari / Firefox users get the same UX they had before Phase 2a.
 */

export interface NativeAudioDevice {
  /** Stable id for the device (AVAudioSessionPortDescription.uid). */
  id: string
  /** Human-readable name — safe to display even before mic
   *  permission is granted, unlike the web getUserMedia path. */
  name: string
  /** High-level classification for UI grouping. Unknown port types
   *  fall through as `'other'` rather than failing enumeration. */
  kind: 'builtin' | 'wired' | 'usb' | 'bluetooth' | 'airplay' | 'car' | 'other'
  /**
   * Total channels the device exposes in the requested scope (input
   * when returned from `listInputDevices`, output when returned from
   * `listOutputDevices`). `0` when the runtime couldn't determine
   * the count (older iOS, unusual virtual device) — UI treats 0 as
   * "unknown" and falls back to assuming stereo.
   */
  channelCount: number
  /**
   * Human-readable channel labels, indexed 0..channelCount-1. When
   * the runtime doesn't expose labels (most iOS devices, some
   * CoreAudio virtual devices) the plugin synthesises "Ch 1", "Ch 2"
   * … so the array length always matches `channelCount`.
   */
  channelNames: string[]
}

/** What the current native runtime can do. JS uses `canSwitchOutput`
 *  to enable/disable the output picker and `outputScope` to word the
 *  helper text honestly ("this changes the system default output"
 *  on Catalyst vs. "per-tab routing" on web). */
export interface NativeAudioCapabilities {
  canSwitchOutput: boolean
  /**
   * Scope of an output-device selection:
   *   - `'system'` — Catalyst; changes the macOS system default
   *     output (affects every app).
   *   - `'app'` — hypothetical per-app routing (not implemented).
   *   - `'per-session'` — web setSinkId-style per-tab routing.
   *   - `'none'` — runtime can't switch outputs.
   */
  outputScope: 'system' | 'app' | 'per-session' | 'none'
  canSwitchInput: boolean
}

export type NativeMicPermissionStatus = 'granted' | 'denied' | 'undetermined'

/** Result of `nativeStopRecording`. Matches the plugin's response
 *  shape verbatim so the hook layer can translate directly into the
 *  existing `AudioRecordingResult` + IndexedDB row. */
export interface NativeRecordingResult {
  durationSec: number
  sampleRate: number
  channelCount: number
  /** Base64-encoded 16-bit LPCM WAV bytes. The JS layer converts
   *  this to a Blob via a single `atob` + `Uint8Array` pass. */
  data: string
  mimeType: 'audio/wav'
}

/** Payload emitted by the plugin every ~250 ms while recording, so
 *  the UI can show a live elapsed timer + peak meter without polling. */
export interface NativeRecordingElapsed {
  elapsedSec: number
  /** Peak magnitude over the last emit window, in dBFS. `-120` for
   *  silence. Values ≥ -6 indicate likely clipping. */
  peakDbFS: number
}

/** Response from `startInputStream` — native reports the actual
 *  sample rate + channel count the tap is delivering so JS can size
 *  its ring buffer + resampler without a second round-trip. */
export interface NativeInputStreamHandle {
  sampleRate: number
  channelCount: number
}

/** Payload of every `inputFrames` event while streaming. `data` is
 *  a base64-encoded Float32 interleaved PCM buffer — `frames *
 *  channelCount * 4` bytes after decoding. Subscribers decode once
 *  per event into a `Float32Array` and forward to the worklet.
 *
 *  `deviceId` identifies the input device this payload originated
 *  from — matches the `deviceId` passed to `startInputStream`, or
 *  `''` for the system default. Subscribers that manage per-device
 *  streams filter events by matching this field to their own id. */
export interface NativeInputFrames {
  deviceId: string
  sampleRate: number
  channelCount: number
  frames: number
  data: string
}

interface BusswerksAudioPlugin {
  listOutputDevices(): Promise<{ devices: NativeAudioDevice[] }>
  listInputDevices(): Promise<{ devices: NativeAudioDevice[] }>
  setOutputDevice(options: { deviceId: string }): Promise<void>
  getCapabilities(): Promise<NativeAudioCapabilities>
  requestMicPermission(): Promise<{ granted: boolean }>
  getMicPermissionStatus(): Promise<{ status: NativeMicPermissionStatus }>
  startRecording(): Promise<void>
  stopRecording(): Promise<NativeRecordingResult>
  isRecording(): Promise<{ recording: boolean }>
  startInputStream(options: {
    deviceId?: string
  }): Promise<NativeInputStreamHandle>
  stopInputStream(): Promise<void>
  addListener(
    event: 'devicesChanged',
    cb: () => void,
  ): Promise<PluginListenerHandle>
  addListener(
    event: 'recordingElapsed',
    cb: (e: NativeRecordingElapsed) => void,
  ): Promise<PluginListenerHandle>
  addListener(
    event: 'inputFrames',
    cb: (e: NativeInputFrames) => void,
  ): Promise<PluginListenerHandle>
}

const BusswerksAudio = registerPlugin<BusswerksAudioPlugin>('BusswerksAudio')

/** True when the native plugin is available (i.e. Capacitor shell
 *  on iOS / iPadOS / Mac Catalyst). Web builds return false and
 *  callers fall back to `navigator.mediaDevices`. */
export function hasNativeAudioPlugin(): boolean {
  return isNativePlatform()
}

/**
 * Enumerate audio outputs via the native plugin. Throws on web;
 * callers should guard with `hasNativeAudioPlugin()`.
 */
export async function nativeListOutputs(): Promise<NativeAudioDevice[]> {
  const { devices } = await BusswerksAudio.listOutputDevices()
  return devices
}

/** Enumerate audio inputs via the native plugin. Throws on web. */
export async function nativeListInputs(): Promise<NativeAudioDevice[]> {
  const { devices } = await BusswerksAudio.listInputDevices()
  return devices
}

/** Ask the native plugin what it can do on the current runtime. JS
 *  uses this to drive the AudioPanel's output picker state + its
 *  helper text. Throws on web; callers should guard with
 *  `hasNativeAudioPlugin()`. */
export async function nativeGetCapabilities(): Promise<NativeAudioCapabilities> {
  return BusswerksAudio.getCapabilities()
}

/** Change the active audio output via the native plugin. On Catalyst
 *  this sets the macOS system default — `outputScope: 'system'` on
 *  the capabilities object. Throws on web. */
export async function nativeSetOutputDevice(deviceId: string): Promise<void> {
  return BusswerksAudio.setOutputDevice({ deviceId })
}

/**
 * Subscribe to route-change / device-hotplug notifications. The
 * listener runs with no arguments; the subscriber should re-call
 * the list methods to pick up the new state.
 *
 * Returns an unsubscribe function. Safe to call on web — it returns
 * a no-op unsubscribe without installing anything.
 */
export function subscribeNativeDeviceChanges(cb: () => void): () => void {
  if (!isNativePlatform()) return () => {}
  let handle: PluginListenerHandle | null = null
  let cancelled = false
  void BusswerksAudio.addListener('devicesChanged', cb).then(h => {
    if (cancelled) {
      void h.remove()
    } else {
      handle = h
    }
  })
  return () => {
    cancelled = true
    void handle?.remove()
  }
}

// ── Recording bridge ───────────────────────────────────────────

/** Prompt for microphone permission. Must be called from a user-
 *  initiated code path (button click) on iOS so the system prompt is
 *  allowed to appear. Resolves `{ granted: false }` on denial rather
 *  than rejecting — the UI surfaces denial as an inline banner. */
export async function nativeRequestMicPermission(): Promise<{
  granted: boolean
}> {
  return BusswerksAudio.requestMicPermission()
}

/** Non-prompting read of the current permission state. Drives the
 *  enabled/disabled state of Record buttons so a denied user sees
 *  the right affordance on load without another prompt. */
export async function nativeGetMicPermissionStatus(): Promise<NativeMicPermissionStatus> {
  const { status } = await BusswerksAudio.getMicPermissionStatus()
  return status
}

/** Begin a new recording. Resolves when the native engine is up and
 *  the tap is installed; rejects when already recording or when the
 *  input node has 0 channels (most likely a simulator without a mic). */
export async function nativeStartRecording(): Promise<void> {
  return BusswerksAudio.startRecording()
}

/** Stop the active recording and return the captured bytes. Result
 *  is base64 WAV; callers decode to a `Uint8Array` → Blob before
 *  feeding the existing IndexedDB sample-row pipeline. */
export async function nativeStopRecording(): Promise<NativeRecordingResult> {
  return BusswerksAudio.stopRecording()
}

/** True when a recording is currently in progress. Cheap to call —
 *  the native side just reads an in-memory flag. Useful for the UI
 *  on app re-entry to restore button state. */
export async function nativeIsRecording(): Promise<boolean> {
  const { recording } = await BusswerksAudio.isRecording()
  return recording
}

/** Subscribe to elapsed-time + peak-level updates while recording.
 *  Native fires every 250 ms. Returns an unsubscribe fn that safely
 *  no-ops on web so callers can use the same hook shape everywhere. */
export function subscribeRecordingElapsed(
  cb: (e: NativeRecordingElapsed) => void,
): () => void {
  if (!isNativePlatform()) return () => {}
  let handle: PluginListenerHandle | null = null
  let cancelled = false
  void BusswerksAudio.addListener('recordingElapsed', cb).then(h => {
    if (cancelled) {
      void h.remove()
    } else {
      handle = h
    }
  })
  return () => {
    cancelled = true
    void handle?.remove()
  }
}

// ── Input streaming bridge ─────────────────────────────────────

/** Open the native input stream. Returns the tap's actual sample
 *  rate + channel count so the worklet can align its ring buffer
 *  to the hardware. Omitting `deviceId` falls through to the OS
 *  default input. Idempotent — calling with the same deviceId
 *  while already streaming is a no-op on the native side. */
export async function nativeStartInputStream(
  options: {
    deviceId?: string
  } = {},
): Promise<NativeInputStreamHandle> {
  return BusswerksAudio.startInputStream(options)
}

/** Close the native input stream. Tears the tap consumer down so
 *  the shared input engine releases the mic indicator if no other
 *  consumers (recorder etc.) are active. */
export async function nativeStopInputStream(): Promise<void> {
  return BusswerksAudio.stopInputStream()
}

/** Subscribe to PCM frame events. Fires ~every 10.7 ms at 48 kHz
 *  with 512 frames per callback. Returns an unsubscribe; no-op on
 *  web. */
export function subscribeInputFrames(
  cb: (e: NativeInputFrames) => void,
): () => void {
  if (!isNativePlatform()) return () => {}
  let handle: PluginListenerHandle | null = null
  let cancelled = false
  void BusswerksAudio.addListener('inputFrames', cb).then(h => {
    if (cancelled) {
      void h.remove()
    } else {
      handle = h
    }
  })
  return () => {
    cancelled = true
    void handle?.remove()
  }
}
